import { useEffect, useId, useRef, useState } from 'react'
import type { JSX } from 'react'

import {
  Button,
  Checkbox,
  Dialog,
  Form,
  Notice,
  Select,
  Text,
  toast,
} from '@buffer-mono/popcorn'
import {
  focusFirstInvalidControl,
  revalidateLogic,
  usePopcornForm,
} from '@buffer-mono/popcorn/forms'

import type { PersonalAccessToken } from '../../hooks'
import {
  DEFAULT_EXPIRY_SECONDS,
  EXPIRY_OPTIONS,
  formatExpiryLabel,
} from '../../constants/tokenExpiry'
import { usePersonalAccessTokenScopes } from '../../hooks'
import { TokenSuccessDialog } from '../TokenSuccessDialog'

import { generateTokenFormSchema } from './GenerateTokenDialog.schema'
import type { GenerateTokenFormValues } from './GenerateTokenDialog.schema'
import styles from './GenerateTokenDialog.module.css'

type GenerateTokenResult =
  | { success: true; token: PersonalAccessToken }
  | { success: false; error: string }

type RevokeTokenResult = { success: true } | { success: false; error: string }

type GenerateTokenInput = {
  name?: string
  expiresIn?: number
  scopes?: string[]
}

interface BaseProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onGenerate: (input?: GenerateTokenInput) => Promise<GenerateTokenResult>
  onSuccess: () => void
  isLoading: boolean
}

interface GenerateModeProps extends BaseProps {
  mode?: 'generate'
}

interface RegenerateModeProps extends BaseProps {
  mode: 'regenerate'
  currentToken: string
  currentKeyName?: string
  onRevoke: (accessToken: string) => Promise<RevokeTokenResult>
}

export type GenerateTokenDialogProps = GenerateModeProps | RegenerateModeProps

const DIALOG_CONFIG = {
  generate: {
    description:
      'Generate a personal access key to connect external tools and automate your Buffer workflow.',
    buttonText: 'Generate API Key',
  },
  regenerate: {
    description:
      'Generating a new key will immediately revoke your current key. Any applications or scripts using the current key will stop working.',
    buttonText: 'Regenerate API Key',
  },
} as const

const getFirstErrorMessage = (error: unknown): string | undefined => {
  if (typeof error === 'string') {
    const message = error.trim()
    return message || undefined
  }

  if (Array.isArray(error)) {
    for (const item of error) {
      const message = getFirstErrorMessage(item)
      if (message) return message
    }

    return undefined
  }

  if (typeof error === 'object' && error !== null && 'message' in error) {
    return getFirstErrorMessage(error.message)
  }

  return undefined
}

const createGenerateTokenInput = (
  values: GenerateTokenFormValues,
): GenerateTokenInput => {
  const keyName = values.keyName.trim()

  return {
    scopes: values.scopeNames,
    ...(keyName && { name: keyName }),
    ...(values.expiresIn && {
      expiresIn: Number.parseInt(values.expiresIn, 10),
    }),
  }
}

export const GenerateTokenDialog = (
  props: GenerateTokenDialogProps,
): JSX.Element => {
  const { open, onOpenChange, onGenerate, onSuccess, isLoading } = props
  const mode = props.mode ?? 'generate'
  const config = DIALOG_CONFIG[mode]
  const currentKeyName =
    props.mode === 'regenerate' ? props.currentKeyName : undefined

  const formRef = useRef<HTMLFormElement>(null)
  const permissionsErrorId = useId()
  const [generatedToken, setGeneratedToken] = useState<string | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const {
    scopes,
    isLoading: areScopesLoading,
    hasError: hasScopesError,
  } = usePersonalAccessTokenScopes()

  const defaultValues: GenerateTokenFormValues = {
    keyName: currentKeyName ?? '',
    expiresIn: DEFAULT_EXPIRY_SECONDS,
    scopeNames: [],
  }

  const form = usePopcornForm({
    defaultValues,
    validationLogic: revalidateLogic({
      mode: 'submit',
      modeAfterSubmission: 'change',
    }),
    validators: {
      onDynamic: generateTokenFormSchema,
    },
    listeners: {
      onChange: () => setSubmitError(null),
    },
    onSubmitInvalid: () => focusFirstInvalidControl(formRef.current),
    onSubmit: async ({ value }) => {
      setSubmitError(null)

      if (props.mode === 'regenerate') {
        const revokeResult = await props.onRevoke(props.currentToken)

        if (!revokeResult.success) {
          setSubmitError(
            'Unable to revoke your existing key. Please try again later.',
          )
          toast.error('Failed to revoke existing key')
          return
        }
      }

      const result = await onGenerate(createGenerateTokenInput(value))

      if (result.success) {
        setGeneratedToken(result.token.accessToken)
      } else {
        setSubmitError(result.error)
        toast.error('Failed to generate key')
      }
    },
  })

  const hasSeededScopes = useRef(false)
  useEffect(() => {
    if (hasSeededScopes.current || scopes.length === 0) {
      return
    }

    form.setFieldValue(
      'scopeNames',
      scopes.map((scope) => scope.name),
      {
        dontRunListeners: true,
        dontUpdateMeta: true,
        dontValidate: true,
      },
    )
    hasSeededScopes.current = true
  }, [form, scopes])

  const handleClose = (): void => {
    if (generatedToken) {
      onSuccess()
    }

    setGeneratedToken(null)
    setSubmitError(null)
    form.reset({
      keyName: currentKeyName ?? '',
      expiresIn: DEFAULT_EXPIRY_SECONDS,
      scopeNames: scopes.map((scope) => scope.name),
    })
    onOpenChange(false)
  }

  if (generatedToken) {
    return (
      <TokenSuccessDialog
        open={open}
        onClose={handleClose}
        token={generatedToken}
      />
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content size="medium">
        <Form
          ref={formRef}
          name="generateTokenForm"
          className={styles.form}
          noValidate
          onSubmit={(): void => {
            void form.handleSubmit()
          }}
        >
          <Dialog.Header>
            <Dialog.Title>Generate API Key</Dialog.Title>
            <Dialog.Description>{config.description}</Dialog.Description>
          </Dialog.Header>
          <Dialog.Body>
            {submitError && <Notice variant="error">{submitError}</Notice>}
            {hasScopesError && (
              <Notice variant="error">
                Unable to load permissions. Please close this dialog and try
                again.
              </Notice>
            )}
            <form.AppField name="keyName">
              {(field) => (
                <field.TextField
                  label={
                    <>
                      Key Name{' '}
                      <span className={styles.optionalHint}>(optional)</span>
                    </>
                  }
                  fieldClassName={styles.formField}
                  placeholder="e.g., My Integration"
                  size="large"
                />
              )}
            </form.AppField>
            <form.AppField name="expiresIn">
              {(field) => (
                <field.SelectField
                  label="Expiration"
                  fieldClassName={styles.formField}
                  triggerProps={{ size: 'large' }}
                >
                  {EXPIRY_OPTIONS.map((option) => (
                    <Select.Item key={option.value} value={option.value}>
                      {formatExpiryLabel(option)}
                    </Select.Item>
                  ))}
                </field.SelectField>
              )}
            </form.AppField>
            {scopes.length > 0 && (
              <form.Field name="scopeNames">
                {(field) => {
                  const invalid = !field.state.meta.isValid
                  const error = getFirstErrorMessage(field.state.meta.errors)

                  return (
                    <Form.Field
                      name={field.name}
                      invalid={invalid}
                      className={styles.formField}
                    >
                      <Form.Control>
                        <Checkbox.Group
                          label="Permissions"
                          className={styles.permissionsGroup}
                        >
                          {error ? (
                            <Form.Error id={permissionsErrorId}>
                              {error}
                            </Form.Error>
                          ) : (
                            <Form.Description>
                              Choose what this key can access. All permissions
                              are selected by default.
                            </Form.Description>
                          )}
                          <div className={styles.scopesList}>
                            {scopes.map((scope, index) => {
                              const isChecked = field.state.value.includes(
                                scope.name,
                              )

                              return (
                                <Checkbox
                                  key={scope.name}
                                  checked={isChecked}
                                  aria-invalid={
                                    invalid && index === 0 ? true : undefined
                                  }
                                  aria-describedby={
                                    invalid && index === 0
                                      ? permissionsErrorId
                                      : undefined
                                  }
                                  onChange={(checked): void => {
                                    if (checked === true && !isChecked) {
                                      field.handleChange([
                                        ...field.state.value,
                                        scope.name,
                                      ])
                                    } else if (checked !== true && isChecked) {
                                      field.handleChange(
                                        field.state.value.filter(
                                          (name) => name !== scope.name,
                                        ),
                                      )
                                    }
                                  }}
                                >
                                  <Text size="sm">
                                    <Text as="span" weight="medium" size="sm">
                                      {scope.name}:
                                    </Text>{' '}
                                    <Text as="span" color="subtle" size="sm">
                                      {scope.description}
                                    </Text>
                                  </Text>
                                </Checkbox>
                              )
                            })}
                          </div>
                        </Checkbox.Group>
                      </Form.Control>
                    </Form.Field>
                  )
                }}
              </form.Field>
            )}
          </Dialog.Body>
          <form.Subscribe selector={(state) => state.isSubmitting}>
            {(isSubmitting) => {
              const submissionInProgress = isSubmitting || isLoading

              return (
                <Dialog.Footer>
                  <Dialog.Close>
                    <Button
                      type="button"
                      variant="tertiary"
                      disabled={submissionInProgress}
                    >
                      Cancel
                    </Button>
                  </Dialog.Close>
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={areScopesLoading || hasScopesError}
                    loading={submissionInProgress}
                  >
                    {config.buttonText}
                  </Button>
                </Dialog.Footer>
              )
            }}
          </form.Subscribe>
        </Form>
      </Dialog.Content>
    </Dialog>
  )
}
